import React, { useCallback, useMemo, useState } from 'react'
import { Checkbox, Divider } from 'antd'
import styles from './escalationConfirm.module.css'
import TextArea from 'antd/es/input/TextArea'
import { AttachmentIcon } from '../../../../../Icons/attachmentIcon'
import { PendingFile } from '../../../../createEscalation.models'
import { useCommentDropzone } from '../../../../createEscalations.hooks'
import { PendingFileRow } from '../../../pendingFileRow/pendingFileRow.index'
import { COMMENT_KEY, FILES_KEY, UPDATE_IN_EMAIL_THREAD_KEY } from '../../../../createEscalation.constants'
import { useInsights } from '../../../../../../Context/insightContext'
interface EscalationConfirmProps {
    onConfirm: (formData: FormData) => void;
    onCancel: () => void;
    title: string;
    placeholderText: string;
    updateStatus?: boolean
}

export const EscalationConfirm = ({ onConfirm, onCancel, title, placeholderText, updateStatus }: EscalationConfirmProps) => {
    const { escalationDialogInfo } = useInsights();
    const isEmailSent = escalationDialogInfo?.escalationDetails?.isEmailSent;

    const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
    const [updateInEmailThread, setUpdateInEmailThread] = useState(false);
    const [addCommentContent, setAddCommentContent] = useState('')

    const {
        getRootProps,
        getInputProps,
        open,
        attachmentButtonTitle,
    } = useCommentDropzone(pendingFiles, setPendingFiles);

    const onRemovePendingFile = useCallback((id: string) => {
        setPendingFiles((prev) => prev.filter((f) => f.id !== id))
    }, [])

    const canRenderCheckbox = isEmailSent && updateStatus;

    const handleConfirm = useCallback(() => {
        const payload = {
            comment: addCommentContent,
            files: pendingFiles.map((f: PendingFile) => f.file),
        }
        const formData = new FormData();
        formData.append(COMMENT_KEY, payload.comment);
        updateStatus && formData.append(UPDATE_IN_EMAIL_THREAD_KEY, updateInEmailThread.toString());
        payload.files?.forEach((file) => formData.append(FILES_KEY, file));
        onConfirm(formData);
    }, [onConfirm, addCommentContent, pendingFiles, updateInEmailThread, updateStatus])

    return (
        <div className={styles.escalationConfirmContainer}>
            <div className={styles.escalationConfirmHeader}>
                <p className={styles.escalationConfirmTitle}>{title}</p>
                <button
                    type="button"
                    onClick={open}
                    className={styles.attachmentButton}
                    aria-label="Attach files"
                    title={attachmentButtonTitle}
                >
                    <AttachmentIcon width={15} height={16} fillColor="#3EAE54" className={styles.escalationAttachmentIcon} />
                </button>
            </div>
            <div
                className={styles.escalationConfirmContent}
                {...getRootProps()}
            >
                <input {...getInputProps()} />
                <TextArea
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    placeholder={placeholderText}
                    classNames={{
                        textarea: styles.escalationConfirmTextArea,
                    }}
                    variant='borderless'
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setAddCommentContent(e.target.value)}
                    value={addCommentContent}
                />
                {pendingFiles.length > 0 && (
                    <>
                        <Divider className={styles.dividerStyles} />
                        <div className={styles.pendingFilesContainer}>
                            {pendingFiles.map((file) => (
                                <PendingFileRow
                                    key={file.id}
                                    file={file}
                                    onRemove={onRemovePendingFile}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
            {canRenderCheckbox && <Checkbox checked={updateInEmailThread} onChange={(e) => setUpdateInEmailThread(e.target.checked)} className={styles.checkboxText}>Update in Email Thread</Checkbox>}
            <div className={styles.escalationConfirmFooter}>
                <button className={styles.cancelText} onClick={onCancel}>Cancel</button>
                <button className={styles.saveText} onClick={handleConfirm}>{updateStatus ? "Update Status" : "Confirm"}</button>
            </div>
        </div>
    )
}
